import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setSubmitting(false);
      formEl.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qmepfnahb7382ovc7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {t('navHome')}
                </Link>
                <span>/</span>
                <span className="text-foreground-700">{t('navAbout')}</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                {t('navAbout')}
              </h1>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    番号本创立于2020年，是一站式番号查询、收录与管理平台。我们致力于为用户提供最全面、最精准的番号数据库检索服务，涵盖全新番号、热门排行、经典回顾、高清资源等丰富分类。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    经过多年的积累与发展，番号本已收录超过12万条番号数据，每日持续更新200条以上。我们的数据团队严格把控内容质量，确保每一条番号信息的准确性和完整性。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                    无论是查找特定番号、系统整理收藏，还是浏览最新资讯，番号本都是您最可靠的选择。操作简便、数据精准、更新及时——这是我们不变的承诺。
                  </p>

                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                    平台优势
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {[
                      { icon: 'ri-database-2-line', title: '海量数据', desc: '收录超过12万条番号信息，覆盖全面' },
                      { icon: 'ri-refresh-line', title: '及时更新', desc: '每日更新200条以上，紧跟最新动态' },
                      { icon: 'ri-search-line', title: '精准搜索', desc: '多维度搜索筛选，快速定位目标番号' },
                      { icon: 'ri-shield-check-line', title: '数据可靠', desc: '专业团队审核，确保信息准确完整' },
                    ].map((item) => (
                      <div key={item.title} className="flex gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                          <i className={`${item.icon} text-sm text-primary-600`}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">{item.title}</h4>
                          <p className="text-xs text-foreground-500">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                    用户口碑
                  </h3>
                  <div className="space-y-4">
                    {[
                      { name: '张先生', role: '资深番号爱好者', text: '番号本是我用过最好用的番号查询工具，数据全面且更新及时，强烈推荐！' },
                      { name: '李先生', role: '番号收藏达人', text: '分类浏览功能非常实用，找番号的效率大大提升了。界面也很简洁美观。' },
                      { name: '王先生', role: '平台老用户', text: '使用番号本已经三年了，见证了平台的不断成长。数据越来越全，功能越来越完善。' },
                    ].map((review) => (
                      <div key={review.name} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100">
                            <i className="ri-user-line text-xs text-accent-600"></i>
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-foreground-900">{review.name}</span>
                            <span className="text-xs text-foreground-400 ml-2">{review.role}</span>
                          </div>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    {t('footerContact')}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队将在1-2个工作日内回复您。
                  </p>

                  <div className="bg-background-100 rounded-xl p-6 border border-background-200/70 mb-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                          <i className="ri-mail-line text-sm text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">电子邮箱</p>
                          <p className="text-sm text-foreground-800">contact@fanhaoben.com</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 shrink-0">
                          <i className="ri-customer-service-line text-sm text-accent-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">客服热线</p>
                          <p className="text-sm text-foreground-800">400-888-0000</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 shrink-0">
                          <i className="ri-time-line text-sm text-secondary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">工作时间</p>
                          <p className="text-sm text-foreground-800">周一至周五 9:00 - 18:00</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="bg-background-100 rounded-xl p-6 border border-background-200/70"
                  >
                    <h3 className="text-base font-semibold text-foreground-900 mb-5">发送消息</h3>

                    {formStatus === 'success' && (
                      <div className="mb-5 p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                        感谢您的留言！我们将在1-2个工作日内回复您。
                      </div>
                    )}
                    {formStatus === 'error' && formError && (
                      <div className="mb-5 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                        {formError}
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          您的姓名 *
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-shadow"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          电子邮箱 *
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-shadow"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-shadow"
                          placeholder="请输入主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          留言内容 *
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-shadow resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      <button
                        type="submit"
                        disabled={submitting}
                        className="whitespace-nowrap w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {submitting ? '提交中...' : t('btnSubmit')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mb-10 max-w-md mx-auto">
                以下是用户最常询问的问题，希望对您有帮助
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-4xl mx-auto">
                {[
                  { q: '番号本收录的番号数据来源是什么？', a: '番号本的数据来源于公开渠道和用户贡献，经过专业团队审核校对后发布，确保信息的准确性和完整性。' },
                  { q: '如何快速搜索到想要的番号？', a: '您可以在首页搜索框直接输入番号编号或关键词进行搜索，也可以通过分类浏览页面按类别筛选查找。' },
                  { q: '番号本的数据更新频率是怎样的？', a: '我们的数据团队每日更新番号信息，确保您在第一时间获取到最新的番号内容。日更新量超过200条。' },
                  { q: '番号本是否收费？', a: '番号本目前完全免费使用，所有番号查询和浏览功能均不收取任何费用。未来如有变动将提前公告。' },
                  { q: '如何反馈数据错误或提出建议？', a: '您可以通过本页面的联系表单或发送邮件至 contact@fanhaoben.com 向我们反馈，我们会及时处理。' },
                  { q: '番号本支持移动端访问吗？', a: '番号本已全面适配移动端设备，您可以通过手机浏览器随时随地进行番号查询和浏览。' },
                ].map((faq, idx) => (
                  <div key={idx} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                      <a id={`faq-${idx + 1}`}></a>
                      {faq.q}
                    </h4>
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于番号本',
            description: '番号本是一站式番号查询、收录与管理平台，提供海量番号数据库检索、分类浏览与详细信息查询服务。',
            mainEntity: {
              '@type': 'Organization',
              name: '番号本',
              description: '专业番号查询与收录平台',
              foundingDate: '2020',
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                email: 'contact@fanhaoben.com',
                telephone: '400-888-0000',
                availableLanguage: ['Chinese'],
              },
            },
          }),
        }} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '番号本收录的番号数据来源是什么？', acceptedAnswer: { '@type': 'Answer', text: '番号本的数据来源于公开渠道和用户贡献，经过专业团队审核校对后发布，确保信息的准确性和完整性。' } },
              { '@type': 'Question', name: '如何快速搜索到想要的番号？', acceptedAnswer: { '@type': 'Answer', text: '您可以在首页搜索框直接输入番号编号或关键词进行搜索，也可以通过分类浏览页面按类别筛选查找。' } },
              { '@type': 'Question', name: '番号本的数据更新频率是怎样的？', acceptedAnswer: { '@type': 'Answer', text: '我们的数据团队每日更新番号信息，确保您在第一时间获取到最新的番号内容。日更新量超过200条。' } },
              { '@type': 'Question', name: '番号本是否收费？', acceptedAnswer: { '@type': 'Answer', text: '番号本目前完全免费使用，所有番号查询和浏览功能均不收取任何费用。未来如有变动将提前公告。' } },
              { '@type': 'Question', name: '如何反馈数据错误或提出建议？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过联系表单或发送邮件至 contact@fanhaoben.com 向我们反馈，我们会及时处理。' } },
              { '@type': 'Question', name: '番号本支持移动端访问吗？', acceptedAnswer: { '@type': 'Answer', text: '番号本已全面适配移动端设备，您可以通过手机浏览器随时随地进行番号查询和浏览。' } },
            ],
          }),
        }} />
      </main>
      <Footer />
    </>
  );
}